import React, { useMemo, useRef, useState, useEffect } from 'react';
import * as THREE from 'three';
import { ModelProps } from './sceneAsset';
import { LIGHT_TYPE } from './lightTemplate';
import { Sphere, useHelper, Line } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { ASSETS_2022_CDN, STUDIO_ID_SUFFIX } from '../../../../util/constants';
import Model from '@src/components/side-bar-menu/nft-assets/Model';
import { AmbientLight } from './Lights/ambient';
import { DirectionalLight } from './Lights/directional';
import { HemisphereLight } from './Lights/hemisphere';
import { PointLight } from './Lights/point';
import { SpotLight } from './Lights/spot';

enum LIGHT_MODELS {
  ambient = `${ASSETS_2022_CDN}/Dragon/QF/light+icons/Ambient_Light.glb`,
  hemisphere = `${ASSETS_2022_CDN}/Dragon/QF/light+icons/Ambient_Light.glb`,
  directional = `${ASSETS_2022_CDN}/Dragon/QF/light+icons/Directional_Light.glb`,
  point = `${ASSETS_2022_CDN}/Dragon/QF/light+icons/Point_Light.glb`,
  spot = `${ASSETS_2022_CDN}/Dragon/QF/light+icons/Spot_Light.glb`,
}

export function LightAsset(props: ModelProps) {
  const {
    asset,
    position,
    rotation,
    instanceId,
    light,
    castShadow,
    receiveShadow,
  } = props;

  if (!light) return <></>;

  const lightRef = light.ref;
  const assetRef = asset.ref;

  useEffect(() => {
    if (lightRef.current.shadow && castShadow) {
      lightRef.current.shadow.needsUpdate = true;
    }
  }, [lightRef, castShadow, light.type]);

  const createLight = () => {
    switch (light.type) {
      case LIGHT_TYPE.AMBIENT:
        return <AmbientLight instanceId={instanceId} light={light} />;
      case LIGHT_TYPE.DIRECTIONAL:
        return (
          <DirectionalLight
            castShadow={castShadow}
            instanceId={instanceId}
            light={light}
          />
        );
      case LIGHT_TYPE.HEMISPHERELIGHT:
        return <HemisphereLight light={light} instanceId={instanceId} />;
      case LIGHT_TYPE.POINTLIGHT:
        return (
          <PointLight
            instanceId={instanceId}
            castShadow={castShadow}
            light={light}
          />
        );
      case LIGHT_TYPE.SPOTLIGHT:
        return (
          <SpotLight
            instanceId={instanceId}
            castShadow={castShadow}
            light={light}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <Sphere
        ref={assetRef}
        key={`${light.type}-light_sphere-${asset.name}-${instanceId}`}
        name={`${light.type}-light_sphere-${STUDIO_ID_SUFFIX}${instanceId}`}
        args={[1, 4, 4]}
        position={position ? position : [0, 0, 0]}
        scale={[1, 1, 1]}
        rotation={rotation}
        castShadow={false}
        receiveShadow={false}
      >
        <meshStandardMaterial
          opacity={0}
          transparent={true}
          depthWrite={false}
        />
        {createLight()}
        <Model
          refer={assetRef}
          name={`${light.type}-Model-${STUDIO_ID_SUFFIX}${instanceId}`}
          castShadow={false}
          receiveShadow={false}
          scale={new THREE.Vector3(1, 1, 1)}
          key={`${light.type}-Model-${asset.name}-${instanceId}`}
          url={LIGHT_MODELS[light.type]}
        />
      </Sphere>
    </>
  );
}
